import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { useContactStore } from "@/store/contactStore";
import { useAuthStore } from "@/store/authStore";
import type { Contact, ContactStatus, CreateContactPayload, IdentifierType } from "@/types/contacts";

const STATUS_FILTERS: (ContactStatus | "All")[] = ["All", "Active", "Unsubscribed", "Bounced", "Pending"];

const STATUS_COLORS: Record<ContactStatus, string> = {
  Active: "bg-green-500/10 text-green-600",
  Unsubscribed: "bg-yellow-500/10 text-yellow-600",
  Bounced: "bg-red-500/10 text-red-600",
  Deleted: "bg-muted text-muted-foreground",
  Pending: "bg-blue-500/10 text-blue-500",
};

function formatDate(iso: string) {
  const d = new Date(iso);
  const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: diffDays > 365 ? "numeric" : undefined });
}

function displayName(c: Contact) {
  return c.fullName?.trim() || c.primaryEmail || c.primaryPhone || c.id.slice(0, 8);
}

interface CreateContactModalProps {
  accountId: string;
  onClose: () => void;
}

function CreateContactModal({ accountId, onClose }: CreateContactModalProps) {
  const { createContact, isSaving, error } = useContactStore();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [marketingEmailOptin, setMarketingEmailOptin] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const identifiers: CreateContactPayload["identifiers"] = [];
    if (email) identifiers.push({ identifierType: "Email" as IdentifierType, value: email, isPrimary: true });
    if (phone) identifiers.push({ identifierType: "Phone" as IdentifierType, value: phone, isPrimary: !email });

    try {
      await createContact(accountId, {
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        identifiers,
        marketingEmailOptin,
      });
      onClose();
    } catch {
      // error shown from store
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">First name</label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Last name</label>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Email <span className="text-muted-foreground">(required if no phone)</span></label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          placeholder="contact@example.com"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Phone</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          placeholder="+1234567890"
        />
      </div>
      <label className="flex items-start gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={marketingEmailOptin}
          onChange={(e) => setMarketingEmailOptin(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          Opted in to marketing emails
          <span className="block text-xs text-muted-foreground">Only tick this if the contact has given consent. Promotional campaigns skip contacts who haven't opted in.</span>
        </span>
      </label>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors">
          Cancel
        </button>
        <Button type="submit" disabled={isSaving || (!email && !phone)}>
          {isSaving ? "Creating…" : "Create contact"}
        </Button>
      </div>
    </form>
  );
}

const PAGE_SIZE = 20;

export default function ContactsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const accountId = user?.currentAccountId ?? user?.ownedAccountId ?? "";

  const { contacts, totalCount, isLoading, error, fetchContacts } = useContactStore();
  const [statusFilter, setStatusFilter] = useState<ContactStatus | "All">("All");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [search]);

  useEffect(() => {
    if (!accountId) return;
    fetchContacts(accountId, {
      page,
      pageSize: PAGE_SIZE,
      status: statusFilter !== "All" ? statusFilter : undefined,
      search: debouncedSearch || undefined,
    });
  }, [accountId, page, statusFilter, debouncedSearch, fetchContacts]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <ProtectedRoute>
      <div className="lw-contacts-list container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="lw-contacts-header flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Contacts</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {totalCount > 0 ? `${totalCount.toLocaleString()} contacts` : "Manage your audience"}
              </p>
            </div>
            <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Contact
            </Button>
          </div>

          {/* Search + Filter bar */}
          <div className="lw-contacts-toolbar flex flex-wrap items-center gap-3 mb-6">
            <div className="relative flex-1 min-w-48 max-w-sm">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, phone…"
                className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {STATUS_FILTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    statusFilter === s
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
                Loading…
              </div>
            </div>
          )}

          {/* Empty */}
          {!isLoading && !error && contacts.length === 0 && (
            <div className="lw-contacts-empty rounded-lg border border-dashed border-border p-16 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="mt-4 text-base font-semibold text-foreground">No contacts found</h3>
              <p className="mt-1.5 text-sm text-muted-foreground max-w-xs mx-auto">
                {debouncedSearch || statusFilter !== "All"
                  ? "Try adjusting your search or filter."
                  : "Add your first contact to start building your audience."}
              </p>
              {!debouncedSearch && statusFilter === "All" && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Contact
                </button>
              )}
            </div>
          )}

          {/* Table */}
          {!isLoading && !error && contacts.length > 0 && (
            <>
              <div className="lw-contacts-table overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Email</th>
                      <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Phone</th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                      <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Created</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {contacts.map((c) => (
                      <tr
                        key={c.id}
                        className="bg-card hover:bg-accent/40 cursor-pointer transition-colors"
                        onClick={() => navigate(`/audience/contacts/${c.id}`)}
                      >
                        <td className="px-4 py-3 font-medium text-foreground">{displayName(c)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{c.primaryEmail ?? <span className="text-muted-foreground/40 italic">—</span>}</td>
                        <td className="hidden sm:table-cell px-4 py-3 text-muted-foreground">{c.primaryPhone ?? <span className="text-muted-foreground/40 italic">—</span>}</td>
                        <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">{c.company ?? <span className="text-muted-foreground/40 italic">—</span>}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[c.status]}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground text-xs">{formatDate(c.createdAt)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-xs text-primary hover:underline">View →</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Page {page} of {totalPages} ({totalCount.toLocaleString()} total)
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="New Contact" size="sm">
        <CreateContactModal accountId={accountId} onClose={() => setShowCreateModal(false)} />
      </Modal>
    </ProtectedRoute>
  );
}
